<template>
  <Screen>
    <AppBar :title="title" />
    <Breadcrumbs
      :links="[
        {
          to: $route('admin.users.index'),
          title: 'Users',
        },
      ]"
      :title="title"
    />
    <div class="card card-body">
      <div class="row">
        <div class="col-lg-6">
          <div
            class="
              mb-3
              d-flex
              align-items-center
              justify-content-between
              flex-wrap
            "
          >
            <h3 class="mb-0">Details</h3>
            <inertia-link
              :href="$route('admin.users.edit', { id: user.id })"
              class="btn btn-sm btn-light"
            >
              Update
            </inertia-link>
          </div>
          <div class="table-responsive">
            <table class="table">
              <tr>
                <th>ID</th>
                <td>{{ user.id }}</td>
              </tr>
              <tr>
                <th>Name</th>
                <td>{{ user.name }}</td>
              </tr>
              <tr>
                <th>Email</th>
                <td>
                  <a :href="`mailto:${user.email}`" v-text="user.email"></a>
                </td>
              </tr>
              <tr>
                <th>Mobile</th>
                <td>
                  <a :href="`tel:${user.mobile}`" v-text="user.mobile"></a>
                </td>
              </tr>
              <tr>
                <th>Role</th>
                <td>
                  <span
                    class="badge badge-pill"
                    :class="{
                      'badge-warning': user.role == 'admin',
                      'badge-primary': user.role == 'user',
                    }"
                    v-text="user.role"
                  ></span>
                </td>
              </tr>
              <tr>
                <th>Status</th>
                <td>
                  <StatusSwitch
                    :dataid="user.id"
                    :status="user.isactive"
                    action="userStatus"
                  />
                </td>
              </tr>
              <tr>
                <th>Payment status</th>
                <td>
                  <StatusSwitch
                    :dataid="user.id"
                    :status="user.payment_done"
                    labelText="Paid/Unpaid"
                    action="userPaymentStatus"
                  />
                </td>
              </tr>
              <tr>
                <th>Streak</th>
                <th>
                  {{ user.streak_count }} <span class="text-dim">streaks</span>
                </th>
              </tr>
              <tr>
                <th>Trees planted</th>
                <th>
                  {{ user.trees_planted }} <span class="text-dim">trees</span>
                </th>
              </tr>
              <tr>
                <th>Support tickets</th>
                <th>
                  {{ user.support_tickets_count }}
                  <inertia-link
                    :href="
                      $route('admin.support.index', {
                        search: '#' + user.id,
                        display: 'all',
                      })
                    "
                    class="ml-2"
                  >
                    <small>View</small>
                    <small class="fas fa-external-link-alt"></small>
                  </inertia-link>
                </th>
              </tr>
              <tr>
                <th>Created at</th>
                <td>{{ user.created_at_human || "-" }}</td>
              </tr>
              <tr>
                <th>Updated at</th>
                <td>{{ user.updated_at_human || "-" }}</td>
              </tr>
              <tr>
                <th>Last used</th>
                <td>{{ user.last_used_human || "-" }}</td>
              </tr>
              <tr>
                <th>Last login</th>
                <td>{{ user.last_login_human || "-" }}</td>
              </tr>
            </table>
          </div>
        </div>
        <div class="col-lg-6" v-if="streaks && getObjLength(streaks)">
          <hr class="d-block d-lg-none" />
          <div
            class="
              mb-3
              d-flex
              align-items-center
              justify-content-between
              flex-wrap
            "
          >
            <h3 class="mb-0">Streaks</h3>
            <button
              type="button"
              class="btn btn-sm btn-light"
              @click.prevent="streakFormOpen = true"
            >
              Add streak
            </button>
          </div>
          <div class="table-responsive" v-if="streaks.data.length > 0">
            <table class="table">
              <thead>
                <th>#</th>
                <th>Completed on</th>
              </thead>
              <tbody>
                <tr v-for="(item, i) in streaks.data" :key="item.id">
                  <td>
                    {{
                      i +
                      1 +
                      (streaks.meta.current_page - 1) * streaks.meta.per_page
                    }}
                  </td>
                  <td v-text="item.created_at_human"></td>
                </tr>
              </tbody>
            </table>
          </div>
          <NoData title="No workouts completed yet" v-else>
            <span></span>
          </NoData>
          <Pagination :rawData="streaks.meta" :onClick="pageChange" />
        </div>
      </div>
      <div v-if="transactions && getObjLength(transactions)">
        <hr />
        <h3>Transactions</h3>
        <div class="table-responsive" v-if="transactions.data.length > 0">
          <table class="table">
            <thead class="thead-light">
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Payment for</th>
                <th>Payment status</th>
                <th>Payment gateway</th>
                <th>Amount</th>
                <th>Paid at</th>
                <th>Created at</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in transactions.data" :key="item.id">
                <td>
                  {{ i + 1 }}
                </td>
                <th v-text="item.token"></th>
                <td v-text="item.type"></td>
                <td>
                  <span
                    class="badge badge-pill"
                    :class="{
                      'badge-success':
                        item.status == 'success' || item.status == 'paid',
                      'badge-danger': item.status == 'failed',
                      'badge-warning': item.status == 'pending',
                    }"
                    v-text="item.status"
                  ></span>
                </td>
                <td>
                  <div class="sub-title">
                    <b>Gateway:</b> {{ item.gateway_used }}
                  </div>
                  <div class="sub-title">
                    <b>Txn ID:</b> {{ item.gateway_txn_id || "-" }}
                  </div>
                  <div class="sub-title">
                    <b>Order ID:</b> {{ item.gateway_order_id || "-" }}
                  </div>
                </td>
                <th v-text="item.amount_formatted"></th>
                <td v-text="item.payment_time_human"></td>
                <td v-text="item.created_at_human"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <NoData title="No transactions" v-else>
          <span></span>
        </NoData>
      </div>
    </div>
    <Modal
      :isOpen.sync="streakFormOpen"
      v-on:close="streakModalClose"
      id="add-streak"
      title="Add streak"
    >
      <div>
        <form action="" method="post" @submit.prevent="addStreak">
          <TextBox
            name="date"
            type="datetime-local"
            label="Enter date you want to add streak for"
            v-model="streakForm.date"
            :disabled="streakForm.processing"
            :error="streakForm.errors.date"
          />
        </form>
      </div>
      <template slot="footer">
        <button
          class="btn btn-danger-outline"
          @click="streakFormOpen = !streakFormOpen"
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          @click.prevent="addStreak"
          :disabled="streakForm.processing"
        >
          <div
            class="spinner-border spinner-border-sm"
            role="status"
            v-if="streakForm.processing"
          ></div>
          <span v-else>Save</span>
        </button>
      </template>
    </Modal>
  </Screen>
</template>
<script>
export default {
  props: {
    streaks: {
      type: [Array, Object],
      default: () => ({}),
    },
    userDetails: {
      type: Object,
      default: () => ({}),
    },
    transactions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      streakForm: this.$inertia.form({
        user_id: 0,
        date: null,
      }),
      streakFormOpen: false,
    };
  },
  methods: {
    pageChange(page) {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          page: page,
        })
      );
    },
    addStreak() {
      this.streakForm.user_id = this.user.id;
      this.streakForm.post(this.$route("admin.users.addstreak"), {
        onSuccess: () => {
          if (this.$page.props.flash.success_msg) {
            this.streakModalClose();
          }
        },
      });
    },
    streakModalClose() {
      this.streakFormOpen = false;

      this.streakForm.reset();
      this.streakForm.clearErrors();
    },
  },
  computed: {
    title() {
      return "User details: " + (this.user.name || "");
    },
    user() {
      return this.userDetails.data || {};
    },
  },
};
</script>